import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, message, Button, Tabs, Dropdown, Menu } from 'antd';
import AddComplexEmailAttachmentModal from './AddComplexEmailAttachmentModal';
import { TabMenuContainer, TabMenuItemContainer, TabMenuHeader,
 OverflowContainer, DropdownDetails } from '../SidebarMenu/styles';
import TemplateMenuItem from '../SidebarMenu/TemplateMenuItem';
import ComplexPreviewModal from '../../components/Modals/ComplexPreviewModal';
import { PointerDiv, AttachmentsTab } from './styles';
import * as palette from '../../styles/variables';
import { EMAIL_SETTINGS_TAB as trans } from '../SidebarMenu/translationValues';
import { COMPLEX_EMAIL_SETTINGS } from './translationValues';
import { convertContractToReact } from '../../utils/TextBlockHelper';
import { downloadHref } from '../../utils/javascript';
import URL from '../../utils/helper';

import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../context/complexDocumentContext';
import {
  CONTEXT_KEYS,
  useArtboardContext,
} from '../../context/complexArtboardContext';
import { useEmailContext, COMPLEX_EMAIL_CONTEXT_KEYS } from '../../context/complexEmailContext';
import { COMPLEX_DOCUMENT_VERSION } from '../../utils/complexDocumentConstants';

import { patchAttachments, deleteAttachment as deleteAttachmentAPI } from '../../utils/services/complexEmailAPI';
import { useVersionContext, VERSION_CONTEXT_KEYS } from '../../context/complexVersionContext';
const PROTECTED = 'passwordProtected';

const { TabPane } = Tabs;
const NAMESPACES = ['EMAIL_SETTINGS_TAB', 'COMPLEX_EMAIL_SETTINGS'];

const ComplexEmailSettingsTab = ({ CurrentDesign, ...props }) => {
  const { t } = useTranslation(NAMESPACES);

  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [addingAttachment, toggleAddingAttachment] = useState(false);

  const lifecycleSelector = useCallback(store => store[VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE], []);
  const [lifeCycle, updateVersionStore] = useVersionContext(lifecycleSelector, 'ComplexEmailSettingsTab');

  const editableSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN], []);
  const [editable] = useArtboardContext(editableSelector, 'ComplexEmailSettingsTab');

  const fromAddressSelector = useCallback(store => store[COMPLEX_EMAIL_CONTEXT_KEYS.FROM_ADDRESS], []);
  const [fromAddressId, emailMethods] = useEmailContext(fromAddressSelector, 'ComplexEmailSettingsTab');

  const subjectSelector = useCallback(store => store[COMPLEX_EMAIL_CONTEXT_KEYS.EMAIL_SUBJECT], []);
  const [subject] = useEmailContext(subjectSelector, 'ComplexEmailSettingsTab');

  const attachmentSelector = useCallback(store => store[COMPLEX_EMAIL_CONTEXT_KEYS.ATTACHMENTS], []);
  const [attachments] = useEmailContext(attachmentSelector, 'ComplexEmailSettingsTab');

  const templateIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID], []);
  const [emailTemplateId] = useDocumentContext(templateIdSelector, 'ComplexEmailSettingsTab');

  const customVarSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [globalVariables] = useDocumentContext(
    customVarSelector,
    'ComplexEmailSettingsTab',
  );

  const openSubjectModal = useCallback(() => {
    emailMethods.set({
      [COMPLEX_EMAIL_CONTEXT_KEYS.OPEN_SUBJECT_MODAL]: true
    })
  }, []);

  const onAddAttachment = useCallback(async (attachmentObj) => {
    let response;
    if (attachmentObj.contentType === 'document') {
      response = await patchAttachments(emailTemplateId, {
        contentType: 'document',
        documentId: attachmentObj.templateId,
        name: attachmentObj.name,
        isEditable: attachmentObj.isEditable,
      });
    } else {
      response = await patchAttachments(emailTemplateId, {
        contentType: 'application/pdf',
        storeId: attachmentObj.storeId,
        name: attachmentObj.name
      });
    }
    console.log({ response });
    if (response?.statusCode === 1302) {
      message.success(t(`COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.ADD_ATTACHMENT_SUCCESSFUL.key}`, COMPLEX_EMAIL_SETTINGS.ADD_ATTACHMENT_SUCCESSFUL.value));
      const newAttachment = {
        attachmentId: response.id,
        contentType: attachmentObj.contentType,
        documentName: attachmentObj.name,
        thumbnail: attachmentObj.templateDetails?.thumbnail,
        passwordProtected: false,
        documentId: attachmentObj.templateId,
        isEditable: attachmentObj?.isEditable,
      };
      emailMethods.set({
        [COMPLEX_EMAIL_CONTEXT_KEYS.ATTACHMENTS]: [...(emailMethods.get()[COMPLEX_EMAIL_CONTEXT_KEYS.ATTACHMENTS] || []), newAttachment],
        [response.id]: Object.assign({}, newAttachment, newAttachment.contentType === 'document' ? {
          documentId: attachmentObj.templateId,
          variableContract: attachmentObj.templateDetails?.variableContract,
          flows: attachmentObj.templateDetails?.flows,
          attachmentBusinessLogics: attachmentObj.templateDetails?.businessLogics,
        } : {
          storeId: attachmentObj.storeId
        })
      });
      updateVersionStore({
        [VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE]: Object.assign({}, lifeCycle, {
          [COMPLEX_DOCUMENT_VERSION.DRAFT]: new Date()
        })
      });
    } else if (response?.statusCode === 1314) {
      message.error(t(`COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.ATTACHMENT_ALREADY_EXISTS.key}`, COMPLEX_EMAIL_SETTINGS.ATTACHMENT_ALREADY_EXISTS.value))
    } else if (response?.statusCode === 5504) {
      message.error(t(`COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.EDITABLE_CANNOT_ATTACHED.key}`, COMPLEX_EMAIL_SETTINGS.EDITABLE_CANNOT_ATTACHED.value))
    } else if (response?.statusCode === 5505) {
      message.error(t(`COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.INTERACTIVE_TEMPLATE_ATTACHED.key}`, COMPLEX_EMAIL_SETTINGS.INTERACTIVE_TEMPLATE_ATTACHED.value))
    } else {
      message.error(t(`COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.ADD_ATTACHMENT_FAILURE.key}`, COMPLEX_EMAIL_SETTINGS.ADD_ATTACHMENT_FAILURE.value))
    }
    toggleAddingAttachment(false);
  }, [emailTemplateId, lifeCycle]);

  const deleteAttachment = useCallback(async (attachmentId) => {
    const response = await deleteAttachmentAPI(attachmentId);
    if (response.statusCode === 1304) {
      message.success(t(`COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.DELETE_ATTACHMENT_SUCCESSFUL.key}`, COMPLEX_EMAIL_SETTINGS.DELETE_ATTACHMENT_SUCCESSFUL.value));
      emailMethods.set({
        [COMPLEX_EMAIL_CONTEXT_KEYS.ATTACHMENTS]: emailMethods.get()[COMPLEX_EMAIL_CONTEXT_KEYS.ATTACHMENTS]
                    .filter(existingAttachment => existingAttachment.attachmentId !== attachmentId)
      }, [attachmentId]);
      updateVersionStore({
        [VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE]: Object.assign({}, lifeCycle, {
          [COMPLEX_DOCUMENT_VERSION.DRAFT]: new Date()
        })
      });
    } else {
      message.error(t(`COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.DELETE_ATTACHMENT_FAILURE.key}`, COMPLEX_EMAIL_SETTINGS.DELETE_ATTACHMENT_FAILURE.value));
    }
  }, [lifeCycle]);

  const protectAttachment = useCallback(async (attachmentId, passwordProtected) => {
    let response = await patchAttachments(emailTemplateId, {
      attachmentId,
      passwordProtected
    });
    if (response.statusCode === 1315) {
      message.success(t(`COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.PASSWORD_PROTECT_SUCCESS.key}`, COMPLEX_EMAIL_SETTINGS.PASSWORD_PROTECT_SUCCESS.value));
      emailMethods.set({
        [COMPLEX_EMAIL_CONTEXT_KEYS.ATTACHMENTS]: emailMethods.get()[COMPLEX_EMAIL_CONTEXT_KEYS.ATTACHMENTS]
                    .map(existingAttachment => {
                      if (existingAttachment.attachmentId === attachmentId) {
                        return Object.assign({}, existingAttachment, {
                          passwordProtected
                        })
                      }
                      return existingAttachment;
                    }),
        [attachmentId]: Object.assign({}, emailMethods.get()[attachmentId], {
          passwordProtected
        })
      }, []);
      updateVersionStore({
        [VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE]: Object.assign({}, lifeCycle, {
          [COMPLEX_DOCUMENT_VERSION.DRAFT]: new Date()
        })
      });
    } else {
      message.error(t(`COMPLEX_EMAIL_SETTINGS:${COMPLEX_EMAIL_SETTINGS.PASSWORD_PROTECT_FAILURE.key}`, COMPLEX_EMAIL_SETTINGS.PASSWORD_PROTECT_FAILURE.value));
    }
  }, [emailTemplateId, lifeCycle]);

  const previewAttachedTemplate = useCallback((e, attachmentObj) => {
    e.stopPropagation();
    setSelectedTemplate(emailMethods.get()[attachmentObj.attachmentId]);
  }, []);

  const clickFileDownload = useCallback((attachmentObj) => {
    const storeId = emailMethods.get()[attachmentObj.attachmentId].storeId;
    downloadHref(`${URL.BASE_URL}store?id=${storeId}`, attachmentObj.name);
  }, []);

  return (
    <>
     <TabMenuContainer className="mailSettings" style={{ paddingTop: 10 }}>
      <TabMenuItemContainer columnDirection={true} subject={1}>
        <TabMenuHeader subject={1} style={{ paddingBottom: 10 }}> {t(trans.SUBJECT_OF_EMAIL_LABEL.key, trans.SUBJECT_OF_EMAIL_LABEL.value)}  </TabMenuHeader>
      <PointerDiv
        editable={false}
        id={`q-container-email-subject`}
        style={{
          width: '100%',
          border: '1px solid black',
          borderRadius: 5,
          background: 'white',
          padding: 5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
        onClick={openSubjectModal}
      >
        <div className='quill'>
          <div className='ql-container ql-snow ql-preview'>
            {convertContractToReact(subject?.contents, undefined, undefined, globalVariables)}
          </div>
        </div>
        <Icon
          type="edit"
          style={{ color: palette.BLACK_COLOR, cursor: 'pointer', fontSize: '18px' }}
          onClick={openSubjectModal}
        />
      </PointerDiv>
      </TabMenuItemContainer>
      </TabMenuContainer>

      <AttachmentsTab>
        {attachments && attachments.length > 0 && (
          <Tabs defaultActiveKey="1">
            {attachments.filter(attachment => attachment.contentType === 'document').length && (
              <TabPane tab="Attachments" key="1">
                <OverflowContainer>
                  <TabMenuItemContainer addAttachment={true} style={{ marginBottom: 0 }}>
                    {attachments
                      .filter(attachment => attachment.contentType === 'document')
                      .map(templateObj => {
                        return (
                          <TemplateMenuItem
                            key={templateObj.id}
                            templateObj={templateObj}
                            onMenuClick={() => { }}
                            onPreviewClick={e => {
                              previewAttachedTemplate(e, templateObj)
                            }}
                            showCheckbox={true}
                            checkboxValue={templateObj[PROTECTED]}
                            onCheck={(e) => {
                              if (editable) {
                                protectAttachment(templateObj.attachmentId, e.target.checked);
                              }
                            }}
                            noCarousel
                            renderDropDown={() => {
                              if (!editable) return <div />
                              return (
                                <DropdownDetails
                                  id={`${templateObj.attachmentId}-template`}
                                  className="dropdown-menu"
                                  onClick={e => e.stopPropagation()}
                                >
                                  <Dropdown
                                    overlay={
                                      <Menu>
                                        <Menu.Item
                                          key="0"
                                          onClick={() => {
                                            deleteAttachment(templateObj.attachmentId);
                                          }}
                                        >
                                          {t(trans.DELETE_ATTACHMENT_DROPDOWN_ACTION.key, trans.DELETE_ATTACHMENT_DROPDOWN_ACTION.value)}
                                        </Menu.Item>
                                      </Menu>
                                    }
                                    trigger={['click']}
                                    getPopupContainer={() =>
                                      document.getElementById(`${templateObj.attachmentId}-template`)
                                    }
                                  >
                                    <Icon type="more" />
                                  </Dropdown>
                                </DropdownDetails>
                              )
                            }}
                            attachmentTemp={true}
                          />
                        );
                      })}
                  </TabMenuItemContainer>
                </OverflowContainer>
              </TabPane>
            )}
            {attachments.filter(attachment => attachment.contentType !== 'document').length && (
              <TabPane tab="Files" key="2">
                <OverflowContainer>
                  <TabMenuItemContainer style={{ marginBottom: 0 }}>
                    {attachments
                      .filter(attachment => attachment.contentType !== 'document')
                      .map(attachment => (
                        <div
                          className={'buttonItem'}
                          onClick={() => {
                            clickFileDownload(attachment);
                          }}
                        >
                          <Icon type={'file-pdf'} />
                          <span className={'overflowText'}> {attachment.documentName} </span>
                          {editable && (
                            <Icon
                              type={'delete'}
                              onClick={e => {
                                e.stopPropagation();
                                e.preventDefault();
                                deleteAttachment(attachment.attachmentId);
                              }}
                            />
                          )}
                        </div>
                      ))}
                  </TabMenuItemContainer>
                </OverflowContainer>
              </TabPane>
            )}
          </Tabs>
        )}
      </AttachmentsTab>
      <TabMenuContainer className="mailSettings" addAttachment={true}>
        {editable && (
          <Button
            className={'addAttachmentsButton'}
            onClick={() => toggleAddingAttachment(true)}
            style={{ marginTop: 10, zIndex: 200 }}
          >
            {t(trans.ADD_TEMPLATE_AS_ATTACHMENT_BUTTON.key, trans.ADD_TEMPLATE_AS_ATTACHMENT_BUTTON.value)}
          </Button>
        )}

        {selectedTemplate && (
          <ComplexPreviewModal
            visible={true}
            onCancel={() => setSelectedTemplate(undefined)}
            type={"publishedDocument"}
            templateId={selectedTemplate.documentId}
          />
        )}
        <AddComplexEmailAttachmentModal
          visible={addingAttachment}
          onCancel={() => toggleAddingAttachment(false)}
          onUpload={onAddAttachment}
        />
      </TabMenuContainer>
    </>
  );
};

export default ComplexEmailSettingsTab;